import styles from './style.module.scss';
import { useTranslation } from 'react-i18next';
import { ResponsiveLine } from '@nivo/line';
import useCharts from './useCharts';
import { Spin, DatePicker, Select, Row, Col } from 'antd';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { BarChart, Bar, Cell } from 'recharts';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import DomainApi from 'libs/api/domains';
import { useDomains } from '../../../redux/domain/use-domains';

const { RangePicker } = DatePicker;
const { Option } = Select;

const MyResponsiveLineDemo = ({ data, availableKeys, isLinear = false }) => (
  <ResponsiveContainer width='100%' height={300}>
    <BarChart
      width={500}
      height={300}
      data={data}
      margin={{
        top: 5,
        right: 30,
        left: 20,
        bottom: 5,
      }}
    >
      <CartesianGrid strokeDasharray='3 3' />
      <XAxis dataKey='name' />
      <YAxis scale={isLinear ? 'linear' : 'log'} domain={['auto', 'auto']} />
      <Tooltip />
      <Legend iconSize={24} />
      {availableKeys.includes('SMS') && (
        <Bar dataKey='SMS' fill='rgb(232, 193, 160)' />
      )}
      {availableKeys.includes('NL Whatsapp') && (
        <Bar dataKey='NL Whatsapp' fill='rgb(244, 117, 96)' />
      )}
      {availableKeys.includes('NL email') && (
        <Bar dataKey='NL email' fill='rgb(190, 186, 218)' />
      )}
      {availableKeys.includes('NL SMS') && (
        <Bar dataKey='NL SMS' fill='rgb(232, 168, 56)' />
      )}
      {availableKeys.includes('Transazionali') && (
        <Bar dataKey='Transazionali' fill='rgb(97, 205, 187)' />
      )}
    </BarChart>
  </ResponsiveContainer>
);

const ChartSection = () => {
  const { t } = useTranslation();
  const { availableKeys, loading, newData } = useCharts();
  const { currentDomain } = useDomains();

  // Local filters state (hook stays untouched)
  const [dateRange, setDateRange] = useState([
    moment().subtract(7, 'days'),
    moment(),
  ]);
  const [chartType, setChartType] = useState('log'); // 'log' | 'linear'
  const [filterLoading, setFilterLoading] = useState(false);
  const [displayData, setDisplayData] = useState([]);

  const api = useMemo(() => new DomainApi(), []);

  // Keep default hook data initially
  useEffect(() => {
    setDisplayData(newData || []);
  }, [newData]);

  // Fetch date-filtered data locally (hook untouched)
  const fetchByDate = async (fromMoment, toMoment) => {
    if (!currentDomain?.id) return;
    try {
      setFilterLoading(true);
      const date_from = fromMoment.format('YYYY-MM-DD');
      const date_to = toMoment.format('YYYY-MM-DD');
      const {
        data: { data },
      } = await api.getAnalytics(currentDomain.id, {
        params: {
          date_from,
          date_to,
          type: 'day',
        },
      });

      const days = Object.keys(data || {});
      let newChartData = days.map((day) => ({
        name: day,
        SMS: data[day].sms || null,
        'NL Whatsapp': data[day].nl_whatsapp || null,
        'NL email': data[day].nl_email || null,
        'NL SMS': data[day].nl_sms || null,
        Transazionali: data[day].transactional || null,
      }));

      newChartData = newChartData.map((item) => {
        const newItem = { ...item };
        // Keep same label formatting as hook
        // eslint-disable-next-line no-undef
        newItem.name = moment(item.name, 'YYYY-MM-DD').format('DD MMMM YY');
        if (!item.SMS) delete newItem.SMS;
        if (!item['NL Whatsapp']) delete newItem['NL Whatsapp'];
        if (!item['NL email']) delete newItem['NL email'];
        if (!item['NL SMS']) delete newItem['NL SMS'];
        if (!item.Transazionali) delete newItem.transactional;
        return newItem;
      });

      setDisplayData(newChartData);
    } catch (e) {
      // Silent fail; fallback data from hook remains
      // console.log(e);
    } finally {
      setFilterLoading(false);
    }
  };

  const calculateTotal = (data) => {
    if (!data || data.length === 0) return 0;

    return data.reduce((total, day) => {
      const dayTotal =
        (day.SMS || 0) +
        (day['NL Whatsapp'] || 0) +
        (day['NL email'] || 0) +
        (day['NL SMS'] || 0) +
        (day.Transazionali || 0);
      return total + dayTotal;
    }, 0);
  };

  const totalEmails = calculateTotal(displayData);

  return (
    <Spin spinning={loading || filterLoading}>
      <div className={styles.card}>
        <p className={styles.title}>{t('Volumi di invio')}</p>
        <div style={{ width: '100%', marginTop: 16, marginBottom: 16 }}>
          <Row gutter={[12, 12]} align='middle' justify='center'>
            <Col>
              <span style={{ marginRight: 8 }}>
                {t('Data dal')} / {t('Data al')}:
              </span>
              <DatePicker
                allowClear={false}
                format='DD/MM/YYYY'
                value={dateRange[0]}
                onChange={(d) => {
                  const next = [d || dateRange[0], dateRange[1]];
                  setDateRange(next);
                  if (next[0] && next[1]) fetchByDate(next[0], next[1]);
                }}
              />
              <DatePicker
                allowClear={false}
                format='DD/MM/YYYY'
                value={dateRange[1]}
                style={{ marginLeft: 8 }}
                onChange={(d) => {
                  const next = [dateRange[0], d || dateRange[1]];
                  setDateRange(next);
                  if (next[0] && next[1]) fetchByDate(next[0], next[1]);
                }}
              />
            </Col>
            <Col>
              <Select
                value={chartType}
                onChange={setChartType}
                style={{ width: 160 }}
              >
                <Option value='log'>{t('Non lineare')}</Option>
                <Option value='linear'>{t('Lineare')}</Option>
              </Select>
            </Col>
          </Row>
        </div>

        <MyResponsiveLineDemo
          availableKeys={availableKeys}
          data={displayData}
          isLinear={chartType === 'linear'}
        />
        <div className={styles.totalContainer}>
          <p className={styles.totalText}>
            {t('Totale')}: {totalEmails.toLocaleString()}
          </p>
        </div>
      </div>
    </Spin>
  );
};

export default ChartSection;
